import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2 } from "lucide-react";
import { tarifasApi, errMsg } from "./api";

const INFO = {
  kimkim_no_prices: "La actividad de KimKim no tiene precios cargados. Crea la estructura de tramos en KimKim la primera vez, pulsa «Sincronizar catálogo» y después «Recalcular».",
  unit_mismatch: "El tarifario está en una unidad (por grupo / por persona) distinta del tipo de precio de la actividad en KimKim. Comprueba en el paso 1 que la actividad emparejada es la correcta o corrige el tipo de precio en KimKim.",
  tiers_mismatch: "Los tramos de pax del tarifario no coinciden con los de KimKim: compara ambas columnas en la tabla. Si KimKim tiene tramos que el tarifario no cubre, ajústalos en KimKim o confírmalos con el proveedor; el sistema no inventa tramos.",
  currency_mismatch: "El tarifario y KimKim están en monedas distintas: revisa la actividad emparejada.",
  ambiguous_rows: "Hay varios precios para el mismo tramo sin temporada indicada: pueden ser modalidades o tramos distintos que la lectura no separó. Revisa el documento original; si son temporadas, indícalo en el tarifario.",
  unreadable: "Algún importe no se pudo leer (TBA, vacío o ilegible). Revisa el documento original; nunca se interpreta como 0.",
  supplements: "Si en cambio deben ir dentro del neto, pídele al proveedor el tarifario con el suplemento incluido o ajusta el precio a mano en KimKim.",
  mixed_units: "La misma actividad mezcla precios por grupo y por persona: revisa cómo se leyó el tarifario.",
  mixed_currencies: "La misma actividad mezcla monedas: revisa cómo se leyó el tarifario.",
  no_rows: "No se leyeron precios de adulto para esta actividad.",
};

export const ReviewActions = ({ provider, issues, onFixed, taxHint }) => {
  const [busy, setBusy] = useState("");
  const [disc, setDisc] = useState("");
  const [tax, setTax] = useState(taxHint != null ? String(taxHint) : "21");
  if (!issues.length) return null;

  const save = async (key, patch) => {
    setBusy(key);
    try {
      await tarifasApi.updateProvider(provider.id, { ...provider, ...patch });
      toast.success("Ficha del proveedor actualizada · recalculando precios…");
      await onFixed();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar"));
    } finally {
      setBusy("");
    }
  };
  const Btn = ({ k, patch, children, disabled }) => (
    <Button size="sm" variant="outline" className="rounded-md h-7 text-xs" disabled={!!busy || disabled} onClick={() => save(k, patch)} data-testid={`review-fix-${k}`}>
      {busy === k ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}{children}
    </Button>
  );

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950 space-y-3" data-testid="review-actions">
      <p className="font-semibold flex items-center gap-1.5 text-sm"><AlertTriangle className="h-4 w-4" /> Qué tienes que revisar antes de poder actualizar</p>
      {issues.includes("basis_unknown") && (
        <div className="space-y-1.5" data-testid="review-basis">
          <p>Ni el tarifario ni la ficha de <b>{provider.name}</b> indican si los precios son <b>netos</b> o <b>públicos (PVP)</b>. Sin ese dato no se calcula el +8 %.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Btn k="basis-net" patch={{ price_basis: "net" }}>Los precios de {provider.name} son netos</Btn>
            <span className="text-muted-foreground">o</span>
            <Input type="number" value={disc} onChange={(e) => setDisc(e.target.value)} placeholder="% dto." className="h-7 w-20 rounded-md text-xs" data-testid="review-discount-input" />
            <Btn k="basis-public" patch={{ price_basis: "public", agency_discount_pct: Number(disc) }} disabled={disc === ""}>Son públicos con este descuento de agencia</Btn>
          </div>
        </div>
      )}
      {issues.includes("public_discount_unknown") && (
        <div className="space-y-1.5" data-testid="review-discount">
          <p>Los precios son públicos pero no consta el <b>descuento de agencia</b> de {provider.name}.</p>
          <div className="flex items-center gap-2">
            <Input type="number" value={disc} onChange={(e) => setDisc(e.target.value)} placeholder="% dto." className="h-7 w-20 rounded-md text-xs" />
            <Btn k="discount" patch={{ agency_discount_pct: Number(disc) }} disabled={disc === ""}>Guardar descuento</Btn>
          </div>
        </div>
      )}
      {issues.includes("taxes_undefined") && (
        <div className="space-y-1.5" data-testid="review-taxes">
          <p>El tarifario dice que los <b>impuestos NO van incluidos</b>{taxHint != null ? <> (indica un <b>{taxHint} % de IVA</b>)</> : null}. Indica cómo tratarlos para {provider.name}:</p>
          <div className="flex flex-wrap items-center gap-2">
            <Btn k="taxes-included" patch={{ taxes_included_default: true }}>No sumar nada: el neto va tal cual a KimKim</Btn>
            <span className="text-muted-foreground">o sumar IVA del</span>
            <Input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className="h-7 w-16 rounded-md text-xs" data-testid="review-tax-input" />
            <Btn k="taxes-add" patch={{ tax_pct: Number(tax) }} disabled={tax === ""}>% al neto antes del +8 %</Btn>
          </div>
        </div>
      )}
      {issues.includes("supplements") && (
        <div className="space-y-1.5" data-testid="review-supplements">
          <p>El tarifario indica <b>suplementos obligatorios aparte</b> (p. ej. recogida desde otra zona, teleférico). Confirma cómo los trata {provider.name}:</p>
          <Btn k="supplements-separate" patch={{ supplements_separate: true }}>Se cobran aparte: no sumarlos al precio (quedan como aviso)</Btn>
        </div>
      )}
      {issues.filter((i) => INFO[i]).map((i) => <p key={i} data-testid={`review-info-${i}`}>• {INFO[i]}</p>)}
      <p className="text-muted-foreground">Al resolver un punto se recalculan las propuestas automáticamente. Nada se escribe en KimKim hasta que pulses «Actualizar en KimKim» y confirmes la simulación.</p>
    </div>
  );
};
